/**
 * Friendship API Client
 */
import { apiClient } from './client';

// Admin Settings Types
export interface FriendshipSettingsResponse {
  id: number;
  feature_enabled: boolean;
  request_cost_coins: number;
  decision_delay_min_seconds: number;
  decision_delay_max_seconds: number;
  auto_accept_min_messages: number;
  auto_request_trust_threshold: number;
  auto_request_comfort_threshold: number;
  auto_request_stage_min: string;
  auto_message_on_accept_enabled: boolean;
  auto_message_chance: number;
  message_delay_min_seconds: number;
  message_delay_max_seconds: number;
  show_system_message_on_request: boolean;
  show_system_message_on_accept: boolean;
  show_system_message_on_reject: boolean;
}

export interface FriendshipStatus {
  is_friend: boolean;
  status: 'none' | 'pending_from_user' | 'pending_from_character' | 'accepted' | 'rejected';
  requested_at: string | null;
  accepted_at: string | null;
  initiated_by: 'user' | 'character' | null;
  can_request: boolean;
  request_cost_coins: number;
  feature_enabled: boolean;
}

export interface FriendshipRequestResponse {
  success: boolean;
  auto_accepted: boolean;
  message: string;
  error?: string;
}

export interface FriendItem {
  character_id: number;
  accepted_at: string | null;
  stage: string;
  trust_level: number;
  // Character details
  character_name: string;
  character_avatar_url: string | null;
  character_status: 'online' | 'offline';
}

export interface PendingRequestItem {
  character_id: number;
  requested_at: string | null;
  message: string | null;
  // Character details
  character_name: string;
  character_avatar_url: string | null;
}

export const friendshipAPI = {
  /**
   * Get friendship status with a character
   */
  getStatus: (characterId: number) =>
    apiClient.get<FriendshipStatus>(`/friendship/${characterId}/status`),

  /**
   * Request friendship (costs coins)
   */
  request: (characterId: number, message?: string) =>
    apiClient.post<FriendshipRequestResponse>(`/friendship/${characterId}/request`, { message }),

  /**
   * Accept a friendship request from character
   */
  accept: (characterId: number) =>
    apiClient.post<{ success: boolean; message: string }>(`/friendship/${characterId}/accept`),

  /**
   * Reject a friendship request from character
   */
  reject: (characterId: number) =>
    apiClient.post<{ success: boolean; message: string }>(`/friendship/${characterId}/reject`),

  /**
   * End an existing friendship
   */
  end: (characterId: number) =>
    apiClient.delete<{ success: boolean; message: string }>(`/friendship/${characterId}`),

  /**
   * Get all friends
   */
  getFriends: () =>
    apiClient.get<FriendItem[]>('/friendship/'),

  /**
   * Get pending requests from characters
   */
  getPending: () =>
    apiClient.get<PendingRequestItem[]>('/friendship/pending'),
};

// Admin API
export const adminFriendshipAPI = {
  /**
   * Get friendship settings (admin only)
   */
  getSettings: () =>
    apiClient.get<FriendshipSettingsResponse>('/admin/friendship/settings'),

  /**
   * Update friendship settings (admin only)
   */
  updateSettings: (data: Partial<FriendshipSettingsResponse>) =>
    apiClient.put<FriendshipSettingsResponse>('/admin/friendship/settings', data),
};
